import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Loader2, AlertCircle, Download, Activity, Sparkles } from 'lucide-react'
import PageTransition from '@/components/PageTransition'
import GlassCard from '@/components/GlassCard'
import ScoreRing from '@/components/ScoreRing'
import CategoryAccordion from '@/components/CategoryAccordion'
import SuggestionCard from '@/components/SuggestionCard'
import StreamingSuggestionCard from '@/components/StreamingSuggestionCard'
import SourceAttachPanel from '@/components/SourceAttachPanel'
import ReRunAuditButton from '@/components/ReRunAuditButton'
import RunLocationBadge from '@/components/RunLocationBadge'
import PrivacyBanner from '@/components/PrivacyBanner'
import { auditsApi } from '@/api/client'
import { useAiSuggestions } from '@/hooks/useAiSuggestions'

type DetailTab = 'scores' | 'ai'

export default function ReportDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [tab, setTab] = useState<DetailTab>('scores')

  const { data: report, isLoading, isError } = useQuery({
    queryKey: ['report', id],
    queryFn: () => auditsApi.getReport(id!),
    enabled: !!id,
  })

  // Hydrates persisted suggestions AND stays subscribed to the STOMP topic so a
  // generation started from this page (SourceAttachPanel) streams in live.
  // initialStreaming=false: nothing is running when a completed report loads.
  const ai = useAiSuggestions(id, { initialStreaming: false })
  const { suggestions, drafts, errors, streaming } = ai

  const showAi = suggestions.length > 0 || drafts.length > 0 || streaming

  return (
    <PageTransition>
      <div className="max-w-3xl mx-auto px-4 py-12">
        <button
          onClick={() => navigate('/reports')}
          className="flex items-center gap-1.5 text-sm transition-colors mb-8"
          style={{ color: 'var(--text-secondary)' }}
        >
          <ArrowLeft size={14} /> Back to reports
        </button>

        {isLoading && (
          <div className="flex justify-center py-20">
            <Loader2 size={32} className="animate-spin" style={{ color: 'var(--accent)' }} />
          </div>
        )}

        {isError && (
          <GlassCard className="text-center">
            <AlertCircle size={36} className="mx-auto mb-3 text-red-400" />
            <p className="text-sm text-red-400">Failed to load report.</p>
          </GlassCard>
        )}

        {report && (
          <div className="space-y-4">
            {/* Header + score rings */}
            <GlassCard>
              <div className="flex items-start justify-between gap-4 mb-6">
                <div className="min-w-0">
                  <p className="text-xs mb-1 flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
                    {report.device}
                    {report.completedAt
                      ? ` · ${new Date(report.completedAt).toLocaleString()}`
                      : ` · ${new Date(report.createdAt).toLocaleString()}`}
                    <RunLocationBadge runLocation={report.runLocation} />
                  </p>
                  <h1 className="text-lg font-semibold break-all">{report.url}</h1>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <ReRunAuditButton report={report} label="Re-run & compare" />
                  <a
                    href={auditsApi.exportHtmlUrl(report.id)}
                    download={`lens-${report.id}.html`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border bg-white/5 border-white/10 hover:border-accent/40 hover:text-accent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    <Download size={12} /> Export HTML
                  </a>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 justify-items-center">
                <ScoreRing score={report.performanceScore}   label="Performance" />
                <ScoreRing score={report.accessibilityScore} label="Accessibility" />
                <ScoreRing score={report.bestPracticesScore} label="Best Practices" />
                <ScoreRing score={report.seoScore}           label="SEO" />
              </div>
            </GlassCard>

            {/* Client-gathered excerpt flow: offer source attachment on any
                completed report that has no suggestions yet and none running. */}
            {report.rawReport && suggestions.length === 0 && drafts.length === 0 && !streaming && (
              <SourceAttachPanel auditId={report.id} onStarted={() => setTab('ai')} />
            )}

            {/* Tab switcher: only when AI suggestions exist */}
            {showAi && (
              <div className="flex gap-1.5 px-1">
                <button
                  type="button"
                  onClick={() => setTab('scores')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                    tab === 'scores'
                      ? 'bg-accent/15 border-accent/40 text-accent'
                      : 'bg-white/5 border-white/10 text-[var(--text-secondary)] hover:border-white/20'
                  }`}
                >
                  <Activity size={12} /> Audit details
                </button>
                <button
                  type="button"
                  onClick={() => setTab('ai')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                    tab === 'ai'
                      ? 'bg-accent/15 border-accent/40 text-accent'
                      : 'bg-white/5 border-white/10 text-[var(--text-secondary)] hover:border-white/20'
                  }`}
                >
                  {streaming
                    ? <Loader2 size={12} className="animate-spin" />
                    : <Sparkles size={12} />} AI suggestions · {suggestions.length}
                </button>
              </div>
            )}

            {/* Scores tab */}
            {(!showAi || tab === 'scores') && report.rawReport && (
              <div className="space-y-2">
                {!showAi && (
                  <h3 className="text-xs font-semibold uppercase tracking-wider px-1 mb-3"
                    style={{ color: 'var(--text-muted)' }}>
                    Audit Details
                  </h3>
                )}
                <CategoryAccordion rawReport={report.rawReport} />
              </div>
            )}

            {/* AI suggestions tab */}
            {showAi && tab === 'ai' && (
              <div className="space-y-3">
                <PrivacyBanner />
                {errors.map((e, i) => (
                  <GlassCard key={`err-${i}`} className="p-4">
                    <p className="text-xs text-red-400 flex items-center gap-2">
                      <AlertCircle size={13} className="shrink-0" /> {e}
                    </p>
                  </GlassCard>
                ))}
                {suggestions.map(s => (
                  <SuggestionCard key={s.id ?? `${s.auditCheckId}-${s.createdAt}`} suggestion={s} />
                ))}
                {drafts.map(d => (
                  <StreamingSuggestionCard key={`draft-${d.auditCheckId}`} draft={d} />
                ))}
                {streaming && drafts.length === 0 && (
                  <div className="flex items-center gap-2 justify-center py-6 text-xs"
                    style={{ color: 'var(--text-secondary)' }}>
                    <Loader2 size={14} className="animate-spin" /> Generating suggestions…
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </PageTransition>
  )
}
